/**
 * PEFSA — Quote Form Controller v1.6
 * Single submit handler for PDP ('pdp'), PLP drawer ('drawer') and homepage slider ('banner').
 *
 * v1.6 — NO-CORS FIX (P0). The fetch no longer uses mode:'no-cors', so the browser
 *   READS the backend reply and shows success ONLY when the Apps Script confirms
 *   (status:'ok'). The body is a FormData, so the request stays "simple" (no CORS
 *   preflight) and the POST always reaches the server even if the reply cannot be
 *   read. A stable idempotency token (request_id) is attached so a re-send of the
 *   same lead can be de-duplicated on the backend. This is the ONLY change vs v1.5;
 *   everything else (shared by PDP/PLP/banner) is untouched.
 *
 * v1.5 — PHONE IS NOW A HARD GATE.
 *   A quote with a broken phone number is a quote the sales team cannot act on,
 *   so the form now REFUSES to submit unless the phone has exactly 10 digits.
 *
 *   · normalizePhone() strips everything that is not a digit and removes the
 *     Mexican country code if the customer typed it (+52 / 52 / 521).
 *   · The check runs on submit AND live on blur, so the customer is told
 *     immediately — not after filling in the whole form.
 *   · THIS is the real gate. The Apps Script also validates, but it can only
 *     FLAG (it cannot reject: with fetch mode:'no-cors' the browser cannot read
 *     the response, so a rejection would silently lose the lead).
 *
 * v1.4 — Attachment security: .doc/.xls removed (macros). Allowed: pdf/docx/xlsx/csv.
 * v1.3 — Listeners registered exactly once (was duplicating on every open).
 * v1.2 — Attachment (banner only) travels as base64 in hidden fields.
 * v1.1 — validate() is context-aware (banner: quantity not required).
 */

(function () {
  'use strict';

  var GAS_URL = 'https://script.google.com/macros/s/AKfycbyLCpbj4agip0nmKKX95c3qVY-CB3Bm0llS_1tJ0r17IEIy2GSeW-BnZFHvtvvSnFXV/exec';

  var MAX_FILE_BYTES = 5 * 1024 * 1024; // 5 MB
  var PHONE_DIGITS   = 10;              // A Mexican phone number. No exceptions.

  // v1.4 — No .doc / .xls: they can carry macros.
  var ALLOWED_EXT  = ['pdf', 'docx', 'xlsx', 'csv'];
  var FORMATS_TEXT = 'Usa PDF, Word (.docx), Excel (.xlsx) o CSV.';

  function setVal(id, v) {
    var el = document.getElementById(id);
    if (el) el.value = v || '';
  }

  function prefixOf(form) {
    return (form.id || '').replace('-quote-form', '');
  }

  // Idempotency token, stable across retries of the SAME submission.
  // Cleared on confirmed success so a genuinely new lead gets a new token.
  function requestIdFor(form) {
    if (!form._pefsaRequestId) {
      form._pefsaRequestId = 'q_' +
        Date.now().toString(36) + '_' +
        Math.random().toString(36).slice(2, 10);
    }
    return form._pefsaRequestId;
  }

  // ── Phone ────────────────────────────────────────────────────
  // Strips everything that is not a digit, then removes the Mexican country code
  // if the customer typed it. Mirrors normalizePhone() in the Apps Script.
  function normalizePhone(raw) {
    var digits = String(raw || '').replace(/\D/g, '');

    if (digits.length === 13 && digits.indexOf('521') === 0) {
      digits = digits.substring(3);
    } else if (digits.length === 12 && digits.indexOf('52') === 0) {
      digits = digits.substring(2);
    }

    return digits;
  }

  function isValidPhone(digits) {
    return digits.length === PHONE_DIGITS;
  }

  // Live feedback while typing: the customer finds out right away, not at the end.
  function initPhoneField(scope) {
    var input = scope.querySelector('[name="telefono"]');
    if (!input || input._pefsaPhoneInit) return;
    input._pefsaPhoneInit = true;

    input.addEventListener('blur', function () {
      var raw = input.value.trim();
      if (!raw) {
        input.style.borderColor = '#d1d5db';
        return;
      }

      var digits = normalizePhone(raw);

      if (isValidPhone(digits)) {
        input.value = digits;                 // normalised: 10 clean digits
        input.style.borderColor = '#16a34a';  // green
      } else {
        input.style.borderColor = '#dc2626';  // red
      }
    });

    input.addEventListener('input', function () {
      input.style.borderColor = '#d1d5db';
    });
  }

  // ── Attribution from sessionStorage ──────────────────────────────────
  function injectUTMs(scope) {
    var map = {
      'utm-source':   'pefsa_utm_source',
      'utm-medium':   'pefsa_utm_medium',
      'utm-campaign': 'pefsa_utm_campaign',
      'utm-content':  'pefsa_utm_content',
      'utm-term':     'pefsa_utm_term',
      'gclid':        'pefsa_gclid',
      'fbclid':       'pefsa_fbclid',
      'referrer':     'pefsa_referrer',
      'landing':      'pefsa_landing_page',
      'source':       'pefsa_source_page'
    };

    var prefix = prefixOf(scope);

    try {
      Object.keys(map).forEach(function (suffix) {
        var el = scope.querySelector('#' + prefix + '-' + suffix);
        if (el) el.value = sessionStorage.getItem(map[suffix]) || '';
      });

      var ua = scope.querySelector('#' + prefix + '-ua');
      if (ua) ua.value = navigator.userAgent;
    } catch (e) {}
  }

  // ── "+ Datos adicionales" toggle — registered exactly once ─────────────────
  function initDetailsToggle(scope) {
    var prefix = prefixOf(scope);
    var toggle = scope.querySelector('#' + prefix + '-details-toggle');
    var extra  = scope.querySelector('#' + prefix + '-details-extra');

    if (!toggle || !extra || toggle._pefsaToggleInit) return;
    toggle._pefsaToggleInit = true;

    toggle.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();

      var isOpen = extra.style.display === 'flex';

      extra.style.display       = isOpen ? 'none' : 'flex';
      extra.style.flexDirection = 'column';

      toggle.textContent = isOpen
        ? '+ Datos adicionales (RFC)'
        : '− Datos adicionales (RFC)';

      toggle.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
    });
  }

  // ── File input feedback — registered exactly once ────────────────────────
  function initFileInput(scope) {
    var prefix = prefixOf(scope);
    var input  = document.getElementById(prefix + '-archivo');
    var msg    = document.getElementById(prefix + '-archivo-msg');

    if (!input || input._pefsaFileInit) return;
    input._pefsaFileInit = true;

    input.addEventListener('change', function () {
      if (!msg) return;

      if (!input.files || !input.files.length) {
        msg.textContent = '';
        return;
      }

      var f   = input.files[0];
      var ext = (f.name.split('.').pop() || '').toLowerCase();
      var mb  = (f.size / 1024 / 1024).toFixed(1);

      if (ALLOWED_EXT.indexOf(ext) === -1) {
        msg.style.color = '#991b1b';
        msg.textContent = 'Formato no permitido. ' + FORMATS_TEXT;
        input.value = '';
        return;
      }

      if (f.size > MAX_FILE_BYTES) {
        msg.style.color = '#991b1b';
        msg.textContent = 'El archivo pesa ' + mb + ' MB. El máximo es 5 MB.';
        input.value = '';
        return;
      }

      msg.style.color = '#166534';
      msg.textContent = '✓ ' + f.name + ' (' + mb + ' MB)';
    });
  }

  // ── Read the file into base64 hidden fields ────────────────────────────
  function encodeAttachment(form) {
    var prefix = prefixOf(form);

    return new Promise(function (resolve, reject) {
      var input = document.getElementById(prefix + '-archivo');

      if (!input || !input.files || !input.files.length) {
        setVal(prefix + '-archivo-b64', '');
        setVal(prefix + '-archivo-nombre', '');
        setVal(prefix + '-archivo-mime', '');
        return resolve();
      }

      var f   = input.files[0];
      var ext = (f.name.split('.').pop() || '').toLowerCase();

      if (ALLOWED_EXT.indexOf(ext) === -1) {
        return reject(new Error('Formato no permitido. ' + FORMATS_TEXT));
      }
      if (f.size > MAX_FILE_BYTES) {
        return reject(new Error('El archivo supera los 5 MB.'));
      }

      var reader = new FileReader();

      reader.onload = function () {
        var b64 = String(reader.result || '').split(',')[1] || '';
        setVal(prefix + '-archivo-b64', b64);
        setVal(prefix + '-archivo-nombre', f.name);
        setVal(prefix + '-archivo-mime', f.type || 'application/octet-stream');
        resolve();
      };

      reader.onerror = function () {
        reject(new Error('No se pudo leer el archivo. Intenta con otro.'));
      };

      reader.readAsDataURL(f);
    });
  }

  // ── Validation ────────────────────────────────────────────────
  function validate(form) {
    var ctx      = form.getAttribute('data-quote-form-context') || '';
    var name     = (form.nombre   ? form.nombre.value.trim()   : '');
    var email    = (form.email    ? form.email.value.trim()    : '');
    var phoneRaw = (form.telefono ? form.telefono.value.trim() : '');
    var qty      = (form.cantidad ? form.cantidad.value.trim() : '');
    var cp       = (form.cp       ? form.cp.value.trim()       : '');
    var notes    = (form.notas    ? form.notas.value.trim()    : '');

    if (!name || !email) {
      alert('Por favor completa tu nombre y tu correo.');
      return false;
    }

    // v1.5 — THE PHONE IS THE GATE.
    // Without a valid phone the sales team cannot call the lead, so the form
    // simply does not submit. Non-negotiable.
    var phone = normalizePhone(phoneRaw);

    if (!phoneRaw) {
      alert('Necesitamos tu teléfono para poder contactarte.');
      if (form.telefono) form.telefono.focus();
      return false;
    }

    if (!isValidPhone(phone)) {
      alert(
        'El teléfono debe tener 10 dígitos.\n\n' +
        'Escribiste ' + phone.length + ' dígito' + (phone.length === 1 ? '' : 's') + '.\n' +
        'Ejemplo: 5512345678'
      );
      if (form.telefono) {
        form.telefono.focus();
        form.telefono.style.borderColor = '#dc2626';
      }
      return false;
    }

    // Normalised before sending: the backend receives 10 clean digits.
    if (form.telefono) form.telefono.value = phone;

    if (ctx === 'banner') {
      if (!notes) {
        alert('Cuéntanos qué material necesitas cotizar.');
        return false;
      }
    } else {
      if (!qty || Number(qty) < 1) {
        alert('Indica la cantidad requerida.');
        return false;
      }
    }

    if (!/^[0-9]{5}$/.test(cp)) {
      alert('El código postal debe tener 5 dígitos.');
      if (form.cp) form.cp.focus();
      return false;
    }

    return true;
  }

  // ── Submit ──────────────────────────────────────────────────
  function handleSubmit(e) {
    e.preventDefault();

    var form   = e.target;
    var prefix = prefixOf(form);

    // Honeypot
    if (form.website && form.website.value.trim() !== '') return;

    if (!validate(form)) return;

    var btn       = form.querySelector('#' + prefix + '-quote-btn');
    var successEl = document.getElementById(prefix + '-quote-success');
    var errorEl   = document.getElementById(prefix + '-quote-error');

    // Double-submit guard
    if (btn && btn.disabled) return;

    if (btn) {
      btn.disabled    = true;
      btn.textContent = 'Enviando…';
    }

    if (errorEl) errorEl.style.display = 'none';

    function fail(message) {
      if (btn) {
        btn.disabled    = false;
        btn.textContent = 'Solicitar cotización';
      }
      if (message) {
        alert(message);
      } else if (errorEl) {
        errorEl.style.display = 'block';
      }
    }

    encodeAttachment(form)
      .then(function () {
        injectUTMs(form);

        var fd = new FormData(form);
        fd.append('request_id', requestIdFor(form)); // idempotency token

        // No mode:'no-cors' — we WANT to read the reply. FormData keeps the
        // request "simple" (no preflight), so the POST reaches the server
        // regardless; redirect:'follow' chases the Apps Script 302 to its
        // content response.
        return fetch(GAS_URL, {
          method:   'POST',
          body:     fd,
          redirect: 'follow'
        });
      })
      .then(function (res) {
        // Read as text first so a non-JSON body does not masquerade as a
        // network error; then try to parse the backend's verdict.
        return res.text().then(function (raw) {
          var data = null;
          try { data = JSON.parse(raw); } catch (e) {}
          return { httpOk: res.ok, data: data };
        });
      })
      .then(function (r) {
        if (r.httpOk && r.data && r.data.status === 'ok') {
          form._pefsaRequestId = null; // token consumed on confirmed success
          form.style.display = 'none';
          if (successEl) successEl.style.display = 'block';
          return;
        }
        // Reached the server but it did not confirm: surface the real reason.
        throw new Error(
          (r.data && r.data.message) ||
          'No pudimos confirmar el envío. Intenta de nuevo o escríbenos por el chat.'
        );
      })
      .catch(function (err) {
        // Real network failure or a CORS-blocked read. The POST is a simple
        // request, so the lead may already be recorded server-side; the stable
        // request_id lets the backend de-duplicate a retry.
        fail(err && err.message ? err.message : '');
      });
  }

  // ── Init — every listener registered exactly once per form ────────────────
  function initForm(form) {
    if (!form) return;

    initDetailsToggle(form);   // own guard
    initFileInput(form);       // own guard
    initPhoneField(form);      // own guard

    if (form.dataset.pefsaInit) return;   // submit listener: ONCE
    form.dataset.pefsaInit = '1';

    form.addEventListener('submit', handleSubmit);
  }

  function init() {
    document.querySelectorAll('form[data-quote-form-context]').forEach(initForm);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.PefsaQuoteForm = {
    initForm: initForm,
    injectUTMs: injectUTMs
  };

})();
